import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import Seo from "../components/Seo";
import GoogleAuthButton from "../components/GoogleAuthButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back");
      const to = loc.state?.from || "/dashboard";
      nav(to);
    } else setError(r.error);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="login-page">
      <Seo title="Sign in — BizzAtlas" description="Sign in to manage your BizzAtlas listings, analytics and ads." />
      <div className="hidden lg:flex flex-col justify-between surface-navy p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-30" />
        <div className="relative">
          <div className="font-display text-3xl">BizzAtlas</div>
          <p className="mt-3 text-zinc-400 text-sm font-mono-tag uppercase tracking-[0.18em]">/ Global B2B Directory</p>
        </div>
        <div className="relative">
          <div className="font-display text-4xl leading-tight">“We doubled qualified leads in 30 days after upgrading to Premium.”</div>
          <div className="mt-6 text-sm text-zinc-400">— Aria Patel, founder · Northwind Studio</div>
        </div>
        <div className="relative font-mono-tag text-[10px] text-zinc-500 uppercase">v1 · login</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div>
            <h1 className="font-display text-3xl tracking-tight">Welcome back.</h1>
            <p className="text-sm text-zinc-500 mt-1">Sign in to manage your listings, analytics and ads.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
          </div>
          {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
          <Button type="submit" className="w-full btn-brand" disabled={busy} data-testid="login-submit-btn">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <div className="flex items-center gap-3 text-xs text-zinc-400 uppercase tracking-widest font-mono-tag">
            <div className="flex-1 h-px bg-zinc-200" />
            or
            <div className="flex-1 h-px bg-zinc-200" />
          </div>
          <GoogleAuthButton />
          <div className="text-sm text-zinc-500 flex items-center justify-between">
            <Link to="/forgot-password" className="underline" data-testid="login-forgot-link">Forgot password?</Link>
            <span>New here? <Link to="/register" className="text-zinc-900 underline">Create an account</Link></span>
          </div>
        </form>
      </div>
    </div>
  );
}
